import React, { useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { Store, ArrowRight, ShieldCheck, FileText, TrendingUp, Package } from "lucide-react";
import { DocHub } from "../components/DocHub";
import { PageHero, Field, SuccessCard } from "../components/FormKit";
import { MARKETPLACE_DOCS } from "../data/marketplaceDocs";
import { EXTERNAL_LINKS } from "../config";
import { api } from "../lib/api";
import { toast } from "sonner";

const STEPS = [
  { icon: FileText, title: "Apply", text: "Submit your seller application on TAVEN." },
  { icon: ShieldCheck, title: "Get Approved", text: "TAVEN reviews your business for quality and fit." },
  { icon: Package, title: "List Products", text: "Onboard to the seller system and submit products." },
  { icon: TrendingUp, title: "Sell & Grow", text: "Reach customers and scale inside the ecosystem." },
];

export const Marketplace = () => {
  const { slug } = useParams();
  const { pathname } = useLocation();

  if (pathname.startsWith("/marketplace/documents")) {
    return (
      <DocHub
        eyebrow="TAVEN Marketplace"
        title="Marketplace Policies & Documents"
        subtitle="Professional seller documentation protecting TAVEN SYSTEM LLC and defining seller responsibilities."
        docs={MARKETPLACE_DOCS}
        basePath="/marketplace/documents"
        activeSlug={slug}
        footerNote="Draft legal-style business documents. Have them reviewed by a licensed attorney before final publication. TAVEN SYSTEM LLC."
      />
    );
  }

  return <MarketplaceHub />;
};

const MarketplaceHub = () => {
  const [f, setF] = useState({ full_name: "", business_name: "", shop_name: "", email: "", phone: "", country: "", product_category: "", number_of_products: "", already_sells_online: "", current_sales_channels: "", message: "" });
  const [done, setDone] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const submit = async (e) => { e.preventDefault(); try { await api.post("/leads/seller", f); setDone(true); toast.success("Application received"); window.scrollTo({ top: 0, behavior: "smooth" }); } catch { toast.error("Please try again."); } };

  return (
    <div data-testid="marketplace-page">
      <PageHero eyebrow="Seller Commerce Hub" title="Sell on the TAVEN Marketplace" subtitle="Join a marketplace ecosystem built for sellers, boutiques, suppliers, and growing brands. Register, submit products, manage your account, and sell through the TAVEN ecosystem.">
        <a href={EXTERNAL_LINKS.SELLER_REGISTRATION_URL} target="_blank" rel="noreferrer" data-testid="seller-register-btn" className="bg-taven-orange text-white px-6 py-3 font-bold uppercase tracking-wider text-sm hover:bg-[#d9561a] transition-colors flex items-center gap-2"><Store className="w-4 h-4" /> Seller Registration</a>
        <a href={EXTERNAL_LINKS.SELLER_LOGIN_URL} target="_blank" rel="noreferrer" data-testid="seller-login-btn" className="border border-white/30 text-white px-6 py-3 font-bold uppercase tracking-wider text-sm hover:bg-white hover:text-black transition-colors">Seller Login</a>
        <a href={EXTERNAL_LINKS.SELLER_DASHBOARD_URL} target="_blank" rel="noreferrer" data-testid="seller-dashboard-btn" className="border border-taven-gold text-taven-gold px-6 py-3 font-bold uppercase tracking-wider text-sm hover:bg-taven-gold hover:text-black transition-colors">Seller Dashboard</a>
      </PageHero>

      <section className="py-14 px-4 md:px-8 lg:px-12 mx-auto max-w-7xl">
        <h2 className="text-2xl md:text-3xl font-black font-display tracking-tighter">How selling on TAVEN works</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          {STEPS.map((s, i) => (
            <div key={s.title} className="p-6 bg-white border border-black/5"><span className="text-xs font-bold text-taven-gold">STEP {i + 1}</span><s.icon className="w-7 h-7 text-taven-gold mt-3" /><h3 className="font-black font-display text-lg mt-2">{s.title}</h3><p className="text-sm text-muted-foreground mt-1">{s.text}</p></div>
          ))}
        </div>
      </section>

      <section className="py-4 px-4 md:px-8 lg:px-12 mx-auto max-w-7xl">
        <div className="bg-taven-black text-white p-8 md:p-10">
          <h2 className="text-2xl font-black font-display">Marketplace Documentation & Policies</h2>
          <p className="text-white/70 mt-2 max-w-2xl">Complete, professional seller policies that protect TAVEN and define seller responsibilities — from the Seller Agreement to compliance, payouts, and dispute resolution.</p>
          <Link to="/marketplace/documents" data-testid="marketplace-docs-link" className="inline-flex items-center gap-2 bg-taven-gold text-black px-6 py-3 font-bold uppercase tracking-wider text-sm mt-5 hover:bg-white transition-colors">View All {MARKETPLACE_DOCS.length} Documents <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>

      <section className="py-14 px-4 md:px-8 lg:px-12 mx-auto max-w-2xl">
        <h2 className="text-2xl font-black font-display text-center mb-2">Apply to Sell on TAVEN</h2>
        <p className="text-center text-muted-foreground mb-6 text-sm">Approved sellers are onboarded to the TAVEN seller marketplace system.</p>
        {done ? <SuccessCard testid="marketplace-success" title="Your seller application has been received." text="TAVEN will review your request." /> : (
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="marketplace-seller-form">
            <Field label="Full Name" name="full_name" value={f.full_name} onChange={set} required />
            <Field label="Business Name" name="business_name" value={f.business_name} onChange={set} />
            <Field label="Shop Name" name="shop_name" value={f.shop_name} onChange={set} />
            <Field label="Email" name="email" type="email" value={f.email} onChange={set} required />
            <Field label="Phone" name="phone" value={f.phone} onChange={set} />
            <Field label="Country" name="country" value={f.country} onChange={set} />
            <Field label="Product Category" name="product_category" value={f.product_category} onChange={set} />
            <Field label="Number of Products" name="number_of_products" value={f.number_of_products} onChange={set} />
            <Field label="Do you already sell online?" name="already_sells_online" value={f.already_sells_online} onChange={set} options={["Yes", "No"]} />
            <Field label="Where do you currently sell?" name="current_sales_channels" value={f.current_sales_channels} onChange={set} />
            <div className="sm:col-span-2"><Field label="Message" name="message" value={f.message} onChange={set} textarea /></div>
            <button className="sm:col-span-2 bg-taven-black text-white font-bold uppercase tracking-wider py-4 hover:bg-taven-orange transition-colors" data-testid="marketplace-submit">Apply to Sell on TAVEN</button>
          </form>
        )}
        <p className="text-center text-sm text-muted-foreground mt-6"><Link to="/sell-on-other-marketplaces" className="text-taven-gold font-semibold underline">Sell on Other Marketplaces →</Link></p>
      </section>
    </div>
  );
};

export default Marketplace;
